import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/common/PageHeader";
import { SectionHeading } from "@/components/common/SectionHeading";
import { AppointmentCTA } from "@/components/sections/AppointmentCTA";
import { Reveal } from "@/components/common/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Smile, ScanLine, Clock, ShieldCheck, HeartPulse, Layers } from "lucide-react";

const benefits = [
  { Icon: Smile, title: "Greater comfort", text: "Digital intraoral scanning replaces messy putty impressions — a calmer, gentler experience for sensitive patients." },
  { Icon: ScanLine, title: "Higher accuracy", text: "Guided planning and CBCT imaging allow precise, conservative treatment that preserves natural tooth structure." },
  { Icon: Clock, title: "Fewer visits", text: "Streamlined digital workflows often mean fewer appointments and faster turnaround for restorations." },
  { Icon: ShieldCheck, title: "Predictable outcomes", text: "Simulate and preview results before treatment, so there are no surprises — only confidence." },
  { Icon: HeartPulse, title: "Safer surgery", text: "CBCT-guided implant placement improves safety and precision, especially in complex cases." },
  { Icon: Layers, title: "Better planning", text: "Full-mouth cases are mapped digitally, so every step is coordinated and intentional." },
];

const equipment = [
  { name: "Digital Dental X-ray", text: "Low-radiation, instant imaging for accurate diagnosis." },
  { name: "CBCT Guided Surgery", text: "3D imaging for precise, safe implant planning." },
  { name: "Intraoral Scanner", text: "Comfortable digital impressions without putty." },
  { name: "Endomotor & Apex Locator", text: "Precise, efficient root canal treatment." },
  { name: "Light Cure Units", text: "Reliable, durable bonding of restorations." },
  { name: "Physiodispensers & Implant Kits", text: "Controlled, sterile surgical workflows." },
];

export default function DigitalDentistry() {
  return (
    <>
      <Seo title="Digital Dentistry" description="Digital dentistry at CDT — explained through patient benefits: comfort, accuracy, fewer visits, predictable outcomes." path="/digital-dentistry" />
      <PageHeader eyebrow="Digital Precision" title="Technology in service of you" subtitle="We invest in digital dentistry for one reason: better experiences and better outcomes for our patients." image="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=1200&q=80" />

      <section className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="What It Means For You" title="Benefits you'll actually feel" subtitle="The value of technology is measured in comfort, safety, and results — not gadgets." />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <Reveal key={b.title} delay={(i % 3) * 0.06}>
                <div className="h-full rounded-[var(--cdt-radius-md)] bg-white border border-[color:var(--cdt-border)] shadow-[var(--cdt-shadow-sm)] p-6">
                  <div className="h-11 w-11 rounded-full bg-[rgba(15,92,90,0.08)] flex items-center justify-center"><b.Icon className="h-5 w-5 text-[var(--cdt-primary)]" /></div>
                  <h3 className="font-display text-lg mt-4 text-[var(--cdt-text)]">{b.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-[var(--cdt-muted)]">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Supporting Technology" title="The tools behind the precision" subtitle="Secondary to the benefits, but here's the technology that makes them possible." />
          <div className="mt-8" data-testid="digital-equipment-accordion">
            <Accordion type="single" collapsible className="w-full">
              {equipment.map((e, i) => (
                <AccordionItem key={e.name} value={`item-${i}`} className="border-[color:var(--cdt-border)]">
                  <AccordionTrigger className="text-left font-medium text-[var(--cdt-text)]">{e.name}</AccordionTrigger>
                  <AccordionContent className="text-[var(--cdt-muted)] leading-7">{e.text}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
      <AppointmentCTA />
    </>
  );
}
